import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CategoryCard, CategoryCardSkeleton } from "@/components/CategoryCard";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { categoryService, type Category } from "@/api/categoryService";

const Categories = () => {
  const {
    data: categories,
    isLoading,
    isError,
  } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: categoryService.getCategories,
  });

  const [query, setQuery] = useState("");
  const filtered = categories?.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      c.title.ar.toLowerCase().includes(q) ||
      c.title.en.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-warmBeige-50">
      <Header />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-warmOrange-600 font-arabic mb-4">
            الفئات
          </h1>
          <p className="text-xl text-gray-600 font-arabic max-w-2xl mx-auto">
            اكتشف مجموعة واسعة من الفئات والمنتجات المتنوعة في يتفرج
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <Input
            type="text"
            dir="rtl"
            className="font-arabic text-right"
            placeholder="دوّر على فئة…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Categories Grid */}
        {isError ? (
          <div className="text-center py-16 text-red-500 font-arabic">
            حدث خطأ في تحميل الفئات
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-[18px] xl:gap-5">
            {Array.from({ length: 8 }).map((_, i) => <CategoryCardSkeleton key={i} />)}
          </div>
        ) : filtered && filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500 font-arabic">
            لا توجد فئات مطابقة لبحثك
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-[18px] xl:gap-5">
            {filtered?.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center mt-16 bg-white rounded-2xl p-8 shadow-lg border border-warmBeige-200">
          <h2 className="text-2xl font-bold text-warmOrange-600 font-arabic mb-4">
            لم تجد ما تبحث عنه؟
          </h2>
          <p className="text-gray-600 font-arabic mb-6">
            تصفح جميع المتاجر للعثور على المنتج المثالي لك
          </p>
          <a href="/stores">
            <button className="bg-warmOrange-500 hover:bg-warmOrange-600 text-white px-8 py-3 rounded-lg font-arabic font-semibold transition-colors duration-300">
              تصفح جميع المتاجر
            </button>
          </a>
        </div>
      </main>
      <Footer variant="minimal" />
    </div>
  );
};

export default Categories;
